import { Badge, Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  JobEntityCount,
  JobState,
  JobUpdate,
  jobRecordsApi,
} from '@texas/api/endpoints/jobRecordsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Locale } from '@texas/i18n/types';
import { createColumnHelper } from '@tanstack/react-table';
import {
  dateFromNow,
  formatDate,
  DateFormat,
} from '@texas/utils/helpers/dateHelper';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchFilterInput } from '../SearchFilterInput';
import { AngularNavLink } from '../shared/AngularNavLink';
import { BodyOverride } from '../shared/BodyOverride';
import { DataTableContainer } from '../shared/dataTable/DataTableContainer';
import { PageBreadcrumb } from '../shared/PageBreadcrumb';
import { JobEntity } from './JobEntity';
import {
  getEntityInitials,
  getEntityTypeLabel,
  getJobStateLabel,
  getJobTypeLabel,
  getStateColors,
} from './jobPresentation';

export function JobDetails({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const {
    data: job,
    refetch,
    loading,
    error,
  } = useApiResource(jobRecordsApi.get);
  const [search, setSearch] = useState('');
  const updateColumns = useMemo(() => jobUpdateColumns(t), [t]);

  useEffect(() => {
    refetch(jobId);

    const interval = window.setInterval(() => {
      refetch(jobId);
    }, 3_000);

    return () => window.clearInterval(interval);
  }, [jobId, refetch]);

  const updates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return job?.updates ?? [];
    return (job?.updates ?? []).filter((update) =>
      [update.message, update.entity?.label, update.entity?.context]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(term)),
    );
  }, [job?.updates, search]);

  if (loading && !job)
    return (
      <Flex minH="70vh" align="center" justify="center">
        <Spinner />
      </Flex>
    );
  if (!job) return null;

  const colors = getStateColors(job.state);
  const stateLabel = getJobStateLabel(job.state, t);
  const showLatestMessage =
    Boolean(job.latestMessage.trim()) &&
    job.latestMessage.trim().toLocaleLowerCase() !==
      stateLabel.trim().toLocaleLowerCase();
  return (
    <BodyOverride>
      <Box
        bg="entityDirectory.canvas"
        minH="calc(100dvh - 2rem)"
        px={{ base: 4, lg: 10 }}
        py={{ base: 6, lg: 8 }}
      >
        <Flex direction="column" maxW="1800px" mx="auto" w="full">
          <PageBreadcrumb
            items={[
              { label: t('jobs.title'), route: 'jobs' },
              { label: `#${job.id}` },
            ]}
            label={t('jobs.breadcrumb')}
          />

          <Flex
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            minH="80px"
          >
            <Flex direction="column" gap="5px">
              <Flex align="center" gap="9px" wrap="wrap">
                <Text
                  color="entityDirectory.text"
                  fontFamily="barlow"
                  fontSize={{ base: '28px', lg: '32px' }}
                  fontWeight="bold"
                  letterSpacing="-0.025em"
                  lineHeight="36px"
                >
                  {getJobTypeLabel(job.type, t)}
                </Text>
                <Badge variant={colors.badgeVariant} fontSize="11px">
                  {stateLabel}
                </Badge>
              </Flex>
              {showLatestMessage && (
                <Text color="entityDirectory.mutedText" fontSize="14px">
                  {job.latestMessage}
                </Text>
              )}
            </Flex>
            <Box flex={1} />
            <AngularNavLink to="jobs" variant="unstyled">
              <Button h="40px" variant="entityDirectorySecondary">
                {t('jobs.backToJobs')}
              </Button>
            </AngularNavLink>
          </Flex>

          <Flex direction="column" gap={5}>
            <Flex
              bg="entityDirectory.surface"
              border="1px solid"
              borderColor="entityDirectory.border"
            >
              <Flex
                direction="column"
                gap={3}
                flex="1.8"
                p={6}
                borderRight="1px solid"
                borderColor="entityDirectory.border"
              >
                <Flex justify="space-between" align="baseline">
                  <Text fontFamily="barlow" fontSize="28px" fontWeight="bold">
                    {job.percentComplete}%
                  </Text>
                  <Text color="entityDirectory.mutedText" fontSize="12px">
                    {t('jobs.entitiesCount', {
                      count: job.entityCounts.reduce(
                        (sum, item) => sum + item.count,
                        0,
                      ),
                    })}
                  </Text>
                </Flex>
                <Box bg="entityDirectory.progressTrack" h="8px">
                  <Box bg={colors.bar} h="8px" w={`${job.percentComplete}%`} />
                </Box>
                <Text color="entityDirectory.mutedText" fontSize="12px">
                  {job.latestMessage}
                </Text>
              </Flex>
              <Metric
                label={t('jobs.columns.started')}
                value={formatDate(Locale.En, job.created, DateFormat.Datetime)}
                detail={dateFromNow(Locale.En, job.created)}
              />
              <Metric
                label={t('jobs.columns.requestedBy')}
                value={job.userName}
                detail={job.userBranch}
              />
              <Metric
                label={t('jobs.jobId')}
                value={`#${job.id}`}
                detail={getJobTypeLabel(job.type, t)}
                last={true}
              />
            </Flex>

            <Flex align="start" gap={6}>
              <Box
                flex={1}
                bg="entityDirectory.surface"
                border="1px solid"
                borderColor="entityDirectory.border"
                minW={0}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  px={5}
                  py={4}
                  borderBottom="1px solid"
                  borderColor="entityDirectory.border"
                >
                  <Flex direction="column" gap={1}>
                    <Text fontFamily="barlow" fontSize="22px" fontWeight="bold">
                      {t('jobs.updates')}
                    </Text>
                    <Text color="entityDirectory.mutedText" fontSize="12px">
                      {t('jobs.updatesDescription')}
                    </Text>
                  </Flex>
                  <Box w="280px">
                    <SearchFilterInput
                      height="42px"
                      placeholder={t('jobs.searchUpdates')}
                      style="entityDirectory"
                      value={search}
                      onChange={setSearch}
                    />
                  </Box>
                </Flex>
                <DataTableContainer
                  bg="entityDirectory.surface"
                  error={error}
                  tableId="job-updates"
                  w="100%"
                  datatable={{
                    columns: updateColumns,
                    data: updates,
                    isLoading: loading && !job,
                    variant: 'entity-directory',
                    rowProps: {
                      getRowId: (update) => String(update.id),
                      uniqueRowStyle: (update) =>
                        isAttentionUpdate(update)
                          ? { bg: 'status.dangerBackground' }
                          : null,
                    },
                  }}
                  footer={
                    <Text color="entityDirectory.mutedText" fontSize="12px">
                      {t('jobs.updatesCount', { count: updates.length })}
                    </Text>
                  }
                  footerVariant="entityDirectoryBordered"
                />
              </Box>
              <Box
                w="300px"
                flexShrink={0}
                bg="entityDirectory.surface"
                border="1px solid"
                borderColor="entityDirectory.border"
              >
                <Flex
                  direction="column"
                  gap={1}
                  p={5}
                  borderBottom="1px solid"
                  borderColor="entityDirectory.border"
                >
                  <Text fontFamily="barlow" fontSize="18px" fontWeight="bold">
                    {t('jobs.entities.title')}
                  </Text>
                  <Text color="entityDirectory.mutedText" fontSize="11px">
                    {t('jobs.entitiesLinked')}
                  </Text>
                </Flex>
                {job.entityCounts.map((item) => (
                  <EntityCount key={item.type} item={item} />
                ))}
                {!job.entityCounts.length && (
                  <Text color="entityDirectory.mutedText" p={5} fontSize="12px">
                    {t('jobs.noEntities')}
                  </Text>
                )}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </BodyOverride>
  );
}

function Metric({
  label,
  value,
  detail,
  last = false,
}: {
  label: string;
  value: string;
  detail: string;
  last?: boolean;
}) {
  return (
    <Flex
      direction="column"
      gap={2}
      flex={1}
      p={6}
      borderRight={last ? undefined : '1px solid'}
      borderColor="entityDirectory.border"
    >
      <Text
        color="entityDirectory.mutedText"
        fontSize="10px"
        fontWeight="bold"
        letterSpacing="0.08em"
      >
        {label.toUpperCase()}
      </Text>
      <Text fontFamily="barlow" fontSize="19px" fontWeight="semibold">
        {value}
      </Text>
      <Text color="entityDirectory.mutedText" fontSize="12px">
        {detail || '—'}
      </Text>
    </Flex>
  );
}

function jobUpdateColumns(t: TFunction) {
  const columnHelper = createColumnHelper<JobUpdate>();

  return [
    columnHelper.accessor('timestamp', {
      cell: (info) => (
        <Text color="entityDirectory.mutedText" fontSize="11px">
          {new Date(info.getValue()).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </Text>
      ),
      enableSorting: false,
      header: t('jobs.columns.time'),
      size: 110,
      minSize: 90,
    }),
    columnHelper.accessor('entity', {
      cell: (info) =>
        info.getValue() ? (
          <JobEntity entity={info.getValue()!} />
        ) : (
          <Text color="entityDirectory.mutedText">—</Text>
        ),
      enableSorting: false,
      header: t('jobs.columns.entity'),
      size: 390,
      minSize: 300,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('message', {
      cell: (info) => info.getValue(),
      enableSorting: false,
      header: t('jobs.columns.update'),
      size: 470,
      minSize: 320,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('state', {
      cell: (info) => {
        const state = info.getValue();
        if (compareEnum(JobState, state, JobState.Running)) return null;

        const colors = getStateColors(state);
        return (
          <Flex justify="center" w="full">
            <Badge variant={colors.badgeVariant}>
              {getJobStateLabel(state, t)}
            </Badge>
          </Flex>
        );
      },
      enableSorting: false,
      header: () => (
        <Text textAlign="center" w="full">
          {t('jobs.columns.status')}
        </Text>
      ),
      size: 130,
      minSize: 110,
    }),
  ];
}

function isAttentionUpdate(update: JobUpdate) {
  return (
    compareEnum(JobState, update.state, JobState.Failed) ||
    compareEnum(JobState, update.state, JobState.Cancelled)
  );
}

function EntityCount({ item }: { item: JobEntityCount }) {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      px={5}
      h="52px"
      borderBottom="1px solid"
      borderColor="entityDirectory.border"
    >
      <Flex
        w="28px"
        h="28px"
        align="center"
        justify="center"
        bg="entityDirectory.badgeBackground"
        border="1px solid"
        borderColor="entityDirectory.border"
        fontFamily="barlow"
        fontSize="9px"
        fontWeight="bold"
      >
        {getEntityInitials(item.type)}
      </Flex>
      <Text pl={2.5} fontSize="12px" fontWeight="semibold">
        {getEntityTypeLabel(item.type, t)}
      </Text>
      <Box flex={1} />
      <Text fontFamily="barlow" fontSize="17px" fontWeight="semibold">
        {item.count}
      </Text>
    </Flex>
  );
}
